import { FC, memo, ChangeEvent, useRef, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  Dialog,
  FormControl,
  InputLabel,
  Select,
  Grid,
  MenuItem,
  TextField,
  DialogTitle,
  Chip,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { Attachment, initialCrmActivityEntity, initialActivityTypeFields } from 'src/types/CRMActivityEntity';
import PushPinIcon from '@mui/icons-material/PushPin';
import useCrm from 'src/hooks/useCrm';
import useAuth from 'src/hooks/useAuth';
import { ActivityType } from 'src/types/activity';
import { LoadingButton } from '@mui/lab';
import SaveIcon from '@mui/icons-material/Save';
import CustomerSearch from 'src/components/search/CustomerSearch';
import { Customer } from 'src/types/customer';
import { ActivityTypeEnum } from 'src/types/activityType';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { ActivityFormState, initialFormState } from 'src/types/activityFormState';
import { GetDefaultShipToNumber, getBuffer } from 'src/contexts/CrmContextFunctions';
import useCustomer from 'src/hooks/useCustomer';
import { ShipTo } from 'src/types/orderHeaderDropdowns';
import useSnackbar from 'src/hooks/useSnackbar';
import useNotification from 'src/hooks/useNotifications';
import orderApi from 'src/api/OrderApi';
import crmApi from 'src/api/CrmApi';
import activeCustomerApi from 'src/api/ActiveCustomerApi';
import ActivityFields from './ActivityFormFields/ActivityFields';

interface ActivityFormProps {
  isOpen: boolean;
}

const ActivityForm: FC<ActivityFormProps> = (props) => {
  const {
    activityTypes,
    editingActivityId,
    activityFormState,
    cancelWithoutSaving,
    attachmentClicked,
    getAllActivityTypes,
    setReloadCellWithThisActivityId,
    setShouldReloadGrid,
    setIsFormOpen,
  } = useCrm();

  const theme = useTheme();
  const { openSnackBar } = useSnackbar();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { customer } = useCustomer();
  const { reloadTaskNotifications } = useNotification();
  const [formCustomer, setFormCustomer] = useState<Customer | null>({ ...customer });
  const [shipTos, setShipTos] = useState<ShipTo[]>([]);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState<Attachment[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isInEditMode = editingActivityId !== null;

  const [formState, setFormState] = useState<ActivityFormState>(initialFormState);
  const defaultShipToNumber: string = GetDefaultShipToNumber(customer, shipTos);
  const activityBaseFormState = {
    customerNumber: customer.number,
    assignedTo: user.id,
    createdByCompanyUserId: user.id,
    shipToNumber: defaultShipToNumber,
  };
  console.log('ActivityForm render');
  const loadShipTos = async (cust) => {
    try {
      if (cust?.numberString) {
        const res = await orderApi.getShipTos(encodeURIComponent(cust.numberString));
        setShipTos(res);
      } else {
        setShipTos(cust.shipTos || []);
      }
    } catch (err) {
      console.error('CrmContext: setFormShipTos', err);
    }
  };

  const initializeNewActivity = useCallback(async () => {
    try {
      setIsLoading(true);
      await getAllActivityTypes();
      await loadShipTos(customer);

      const { title, dueDate, sfaOrderId, shipToNumber } = activityFormState.formActivity;
      const newActivity = {
        ...initialCrmActivityEntity,
        customerNumber: customer.number,
        assignedTo: user.id,
        createdByCompanyUserId: user.id,
        ...(title === 'Follow Up' ? { title, dueDate, sfaOrderId, shipToNumber } : {}),
      };

      setFormState((prevState) => ({
        ...prevState,
        formActivity: newActivity,
      }));
    } catch (error) {
      console.error('Error initializing new activity:', error);
    } finally {
      setIsLoading(false);
    }
  }, [customer, user.id]);

  const loadEditActivity = useCallback(async () => {
    try {
      setIsLoading(true);
      const crmActivity = await crmApi.getCrmActivity(editingActivityId);
      if (crmActivity?.customerNumber) {
        const cust = await activeCustomerApi.getCustomer(crmActivity.customerNumber.toString());
        await loadShipTos(cust);
      }
      setFormState((prevState) => ({
        ...prevState,
        formActivity: crmActivity,
      }));
    } catch (error) {
      openSnackBar(`Error: ${error}`, 'error', 10000);
      console.error('Error loading activity for editing:', error);
    } finally {
      setIsLoading(false);
    }
  }, [editingActivityId]);

  useEffect(() => {
    if (isInEditMode) {
      loadEditActivity();
    } else {
      initializeNewActivity();
    }
  }, [isInEditMode]);

  const handleUploadAttachmentClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onShipToChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState((prevState) => ({ ...prevState, formActivity: { ...prevState.formActivity, shipToNumber: e.target.value } }));
  };

  const handleFileUpload = async (e) => {
    if (e.target.files) {
      setFormState((prevState) => ({ ...prevState, formActivity: { ...prevState.formActivity, attachments: Array.from(e.target.files) } }));
    }
  };

  const uploadAttachments = async (files: File[], activityId: number) => {
    if (!files || files.length === 0) return;

    for (let file of files) {
      try {
        const data = await getBuffer(file);
        const formData = new FormData();
        formData.append('file', file);
        const fileType = data.toString().split(',')[0];
        const response = await crmApi.saveAttachment(activityId, fileType, formData, formState.formActivity.customerNumber ?? 0);

        setFormState((prevState) => ({ ...prevState, formActivity: { ...prevState.formActivity, attachments: response.attachments } }));

        if (isInEditMode) {
          setReloadCellWithThisActivityId(activityId);
        }
      } catch (error) {
        console.error('Error:', error);
        openSnackBar(`Error uploading attachment: ${error}`, 'error', 10000);
      }
    }
  };

  const handleDeleteAttachment = (item) => {
    setAttachmentsToDelete([...attachmentsToDelete, item].filter((a, i, arr) => arr.indexOf(a) === i));
    setFormState((prev) => ({ ...prev, formActivity: { ...prev.formActivity, attachments: prev.formActivity.attachments?.filter((a) => a.name !== item.name) } }));
  };

  const deleteAttachments = async (attachments: Attachment[]) => {
    try {
      for (const attachment of attachments) {
        if (attachment.activityId) {
          const res = await crmApi.deleteAttachment(attachment);
          setFormState((prev) => ({
            ...prev,
            formActivity: { ...prev.formActivity, attachments: res.attachments },
          }));
          if (attachment.activityId !== 0) {
            setReloadCellWithThisActivityId(attachment.activityId);
            setShouldReloadGrid(true);
          }
        }
      }
    } catch (err) {
      console.error('Error:', err);
      openSnackBar(`Error deleting attachment: ${err}`, 'error', 10000);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Delete attachments
      if (attachmentsToDelete.length) await deleteAttachments(attachmentsToDelete);

      // Save activity
      const res = await crmApi.upsertActivity({
        ...formState.formActivity,
        ...activityBaseFormState,
      });

      if (isInEditMode) setReloadCellWithThisActivityId(res.activityId);

      openSnackBar(`Updated: ${res.title}`, 'success', 1500);

      // Upload new attachments
      const newAttachments = formState.formActivity.attachments.filter((attachment) => !attachment.activityId);
      if (newAttachments.length > 0) {
        await uploadAttachments(newAttachments as File[], res.activityId);
      }
    } catch (err) {
      console.error('CrmContext: saveActivity', err);
      openSnackBar(`Error updating activity: ${err.message}`, 'error', 20000);
    } finally {
      setIsFormOpen(false);
      reloadTaskNotifications();
      setShouldReloadGrid(true);
      setFormState(initialFormState);
      setAttachmentsToDelete([]);
      setIsSaving(false);
    }
  };

  const resetActivityTypeFields = () => {
    setFormState((prevState) => ({
      ...prevState,
      formActivity: {
        ...prevState.formActivity,
        ...initialActivityTypeFields,
      },
    }));
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    if (fieldName === 'activityTypeId') {
      resetActivityTypeFields();
    }
    setFormState((prevState) => ({
      ...prevState,
      formActivity: {
        ...prevState.formActivity,
        [fieldName]: value,
      },
    }));
  };

  return (
    <Dialog maxWidth={'md'} fullWidth open={props.isOpen} onClose={() => cancelWithoutSaving()}>
      <form className="px-3 pb-2 activity-form" onSubmit={handleSubmit}>
        <DialogTitle style={{ height: '8px', paddingLeft: 0, marginBottom: '15px' }} id="add-custom-item-dialog">
          <b>
            {isInEditMode ? 'Edit Activity' : 'Add Activity'} <>{isLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}</>
          </b>
          <IconButton
            aria-label="close"
            onClick={() => cancelWithoutSaving()}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (t) => t.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <FormControl size="small" fullWidth>
              <CustomerSearch
                className="activity-form-customer-search"
                defaultSearchAll={true}
                defaultCustomer={formCustomer}
                onCustomerSelected={(c: Customer) => setFormCustomer(c)}
                placeholder="Activity Customer"
              />
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControl size="small" fullWidth>
              <TextField
                id="shipping-to-order"
                select
                name="shipToNumber"
                onChange={(e) => onShipToChange(e)}
                value={formState.formActivity.shipToNumber || ''}
                label="Ship To"
                fullWidth
                SelectProps={{
                  native: false,
                }}
              >
                {shipTos.map((x, i) => (
                  <MenuItem key={i} value={x.shipNo}>
                    {x.description}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Activity Type</InputLabel>
              <Select
                disabled={isInEditMode}
                name="activityType"
                label="Activity Type"
                value={formState.formActivity.activityTypeId}
                onChange={(e) => handleFieldChange('activityTypeId', e.target.value as ActivityTypeEnum)}
              >
                {activityTypes.map((activityType: ActivityType, i) => {
                  return (
                    <MenuItem key={i} value={activityType.activityTypeId}>
                      {activityType.activityType}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField label="Title" fullWidth value={formState.formActivity.title || ''} required onChange={(e) => handleFieldChange('title', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            {isMobileDevice ? (
              <Editor
                tinymceScriptSrc="/tinymce/tinymce.min.js"
                init={{
                  height: '200px',
                  width: '100%',
                  menubar: false,
                  statusbar: false,
                  content_style: 'body { font-size:14px; line-height:0.8 }',
                  plugins: ['preview', 'fullscreen'],
                  toolbar: 'bold italic fullscreen',
                }}
                value={formState.formActivity.content}
                onEditorChange={(val) => handleFieldChange('content', val)}
              />
            ) : (
              <Editor
                tinymceScriptSrc="/tinymce/tinymce.min.js"
                init={{
                  height: '200px',
                  width: '100%',
                  menubar: false,
                  statusbar: false,
                  content_style: 'body { font-size:16px; line-height:1 }',
                  plugins: ['advlist', 'autolink', 'lists', 'charmap', 'preview', 'fullscreen', 'table', 'emoticons'],
                  toolbar: `
              formatselect | bold italic forecolor backcolor | 
              alignleft aligncenter alignright | 
              bullist numlist emoticons fullscreen | preview table charmap
            `,
                }}
                value={formState.formActivity.content || ''}
                onEditorChange={(val) => handleFieldChange('content', val)}
              />
            )}
          </Grid>

          <ActivityFields formState={formState} handleFieldChange={handleFieldChange} />

          {formState.formActivity.attachments && (
            <Grid size={{ xs: 12 }}>
              <Box className="activity-form__attachment-container">
                <Button className="activity-form__button" sx={{ mx: 0.5 }} title="Attach Document" variant="outlined" onClick={handleUploadAttachmentClick}>
                  <AttachFileIcon fontSize="small" />
                </Button>
                {formState.formActivity.attachments?.map((item, i) => {
                  return <Chip label={item.name} key={i} sx={{ m: 0.5 }} onClick={() => attachmentClicked(item)} onDelete={() => handleDeleteAttachment(item)} />;
                })}
              </Box>
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <Box>
              <>
                {isSaving ? (
                  <LoadingButton loading loadingPosition="start" startIcon={<SaveIcon />} variant="contained">
                    Saving...
                  </LoadingButton>
                ) : (
                  <Button variant="contained" type="submit">
                    {isInEditMode ? 'Update' : 'Create'}
                  </Button>
                )}
              </>

              <input
                hidden
                type="file"
                id="crm-file-upload"
                name="crm-file-upload"
                accept="image/*, .pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .txt"
                onChange={handleFileUpload}
                ref={fileInputRef}
                multiple
              />

              <Button
                variant={formState.formActivity.isPinned ? 'contained' : 'outlined'}
                onClick={() => handleFieldChange('isPinned', !formState.formActivity.isPinned)}
                sx={{ mx: 2 }}
              >
                {formState.formActivity.isPinned ? (
                  <>
                    <PushPinIcon fontSize="small" sx={{ ml: 0 }} /> Unpin
                  </>
                ) : (
                  <>
                    <PushPinIcon fontSize="small" sx={{ ml: 0 }} /> Pin
                  </>
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Dialog>
  );
};

/**
 * A form component for creating or editing CRM activities, such as notes, tasks, calls, emails, and meetings.
 * It allows the user to select the activity type, set the customer, and enter relevant details based on the selected type.
 *
 *
 */
export default memo(ActivityForm);
